import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import TimerIcon from '@mui/icons-material/Timer';
import { Box, Card, Checkbox, Chip, Grid, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { isAdmin } from '../../utils/authUtils';
import { FileTypes } from '../../utils/constants';
import { formatDate, formatDuration, formatUserName, formatVideoName } from '../../utils/formatUtils';
import {
    getBookLanguageChipColor,
    getBookLanguageChipLabel,
    getBookLanguageStageLabel,
    getChipColor,
    getChipLabel,
    getEffectiveTextProcessStage,
    getProcessStageLabel,
    getTextChipColor,
    getTextChipLabel,
    isSourceLanguage,
} from '../../utils/processStageUtils';
import FileTypeIcon from '../common/FileTypeIcon';
import LanguagePopover from '../common/LanguagePopover';
import { AudioFailedChip, TaskChipIndicator, VideoFailedChip } from '../tasks/TaskIndicators';
import useTaskSummary from '../tasks/useTaskSummary';
import { NAV_ICON_TEXT_GAP } from './navigationSpacing';
/**
 * List item component for displaying files in list view
 *
 * @param {Object} props Component props
 * @param {Object} props.file The file object to display
 * @param {Function} props.onFileClick Handler for when the file is clicked
 * @param {Function} props.onMenuOpen Handler for when the context menu is opened
 * @param {boolean} props.showPath Whether to display the file path (for search results)
 */
const FileListItem = ({ file, onVideoFileClick, onTextFileClick, onAudioFileClick, onBookFileClick, onMenuOpen, showPath, type, isSelectMode = false, isSelected = false, onSelectToggle }) => {
    const theme = useTheme();
    // Extract and format the parent path for display
    const displayPath = React.useMemo(() => {
        if (!showPath || !file.path) return null;

        // Format the path for display
        const pathParts = file.path.split('/').filter(Boolean);
        const dp = '/' + pathParts.join('/');
        return dp;
    }, [file.path, showPath]);

    // Handle click events
    const handleClick = () => {
        if (isSelectMode) {
            const fileKey = `${type}-${file.data}`;
            if (onSelectToggle) onSelectToggle(fileKey, { ...file, type });
            return;
        }
        if (type === FileTypes.VIDEO) {
            if (onVideoFileClick) {
                onVideoFileClick(file);
            }
        } else if (type === FileTypes.TEXT) {
            if (onTextFileClick) {
                onTextFileClick(file);
            }
        } else if (type === FileTypes.AUDIO) {
            if (onAudioFileClick) {
                onAudioFileClick(file);
            }
        } else if (type === FileTypes.BOOK) {
            if (onBookFileClick) {
                onBookFileClick(file);
            }
        }
    };

    const handleMenuClick = (event) => {
        // Stop propagation to prevent file click
        //console.log('TOO - Menu Click', file);
        event.stopPropagation();

        if (onMenuOpen) {
            onMenuOpen(event, file);
        }
    };

    // Get updated by information
    const updatedBy = file.updatedBy || 'Unknown';

    // Get language information with process stages
    const languagesWithStatus = file.languages || [];

    // Clean up the language state management
    const [languagePopover, setLanguagePopover] = React.useState({
        open: false,
        anchorEl: null,
    });

    const handleLanguageCountClick = (event) => {
        // Stop propagation to prevent opening the file
        event.stopPropagation();
        event.preventDefault();

        // Set the anchor element for the popover
        setLanguagePopover({
            open: true,
            anchorEl: event.currentTarget,
        });
    };

    const handleLanguagePopoverClose = () => {
        setLanguagePopover({
            open: false,
            anchorEl: null,
        });
    };

    // Add this function inside your FileGridItem component, after the languagesWithStatus declaration
    const getFilteredLanguages = () => {
        try {
            // Get user info from localStorage
            const userInfo = JSON.parse(localStorage.getItem('authUser')) || {};
            const userLanguageAccess = userInfo.languageAccess || {};

            // If admin, show all languages
            if (isAdmin()) {
                return languagesWithStatus;
            }

            // For non-admins, filter languages
            return languagesWithStatus.filter((lang) => {
                // Get language name from lang.name
                const langName = lang.name;

                if (!langName) return false;

                // Always show English and Gujarati (case insensitive)
                const langNameLower = langName.toLowerCase();
                if (langNameLower === 'english' || langNameLower === 'gujarati') {
                    return true;
                }

                // Show languages where user has write access
                return userLanguageAccess[langName] && userLanguageAccess[langName].includes('WRITE');
            });
        } catch (error) {
            console.error('Error filtering languages:', error);
            return languagesWithStatus; // Return all languages if there's an error
        }
    };

    // Get filtered languages
    // A language's process stage isn't for AV (6j-6)
    const avOnly = !!useTaskSummary()?.avOnly;
    const filteredLanguages = avOnly ? [] : getFilteredLanguages();

    return (
        <Card
            sx={{
                mb: 0, // Remove bottom margin as we're using gap in parent
                p: 1, // Increase padding from 1 to 1.5
                cursor: 'pointer',
                transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out, background-color 0.15s ease-in-out',
                ...(isSelected && {
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.08)',
                    outline: '2px solid',
                    outlineColor: 'error.main',
                }),
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                },
            }}
            onClick={handleClick}
            className="file-list-item"
        >
            <Grid
                container
                spacing={2}
                className="file-folder-item"
                alignItems="center"
                sx={{
                    px: 1,
                    py: 0.25,
                }}
            >
                <Grid item xs={12} sm={4} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: NAV_ICON_TEXT_GAP }} className="file-folder-name">
                        {isSelectMode && (
                            <Checkbox
                                checked={isSelected}
                                size="small"
                                color="error"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const fileKey = `${type}-${file.data}`;
                                    if (onSelectToggle) onSelectToggle(fileKey, { ...file, type });
                                }}
                                sx={{ p: 0.25, mr: 0.5 }}
                            />
                        )}
                        <FileTypeIcon type={type} />
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="body1" noWrap title={file.videoName || file.name}>
                                {formatVideoName(file.videoName || file.name)}
                            </Typography>

                            {file.data && type === FileTypes.VIDEO && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    Video ID: {file.data}
                                </Typography>
                            )}
                            {file.data && type === FileTypes.TEXT && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    Text ID: {file.data}
                                </Typography>
                            )}
                            {file.data && type === FileTypes.AUDIO && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    Audio ID: {file.data}
                                </Typography>
                            )}
                            {file.data && type === FileTypes.BOOK && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    Book ID: {file.data}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Display path in search results */}
                    {showPath && displayPath && (
                        <Tooltip title={displayPath} arrow>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 4, mt: 0.5, display: 'block', wordBreak: 'break-all' }}
                            >
                                <FolderOpenIcon sx={{ fontSize: '0.8rem', mr: 0.5, verticalAlign: 'text-bottom' }} />
                                {displayPath}
                            </Typography>
                        </Tooltip>
                    )}
                </Grid>

                <Grid item xs={6} sm={3} md={2}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} className="folder-file-details">
                        {type === FileTypes.VIDEO && (
                            <>
                                <VideoFailedChip file={file} />
                                {filteredLanguages && filteredLanguages.length > 0 ? (
                                    filteredLanguages.slice(0, 3).map((lang) => (
                                        <React.Fragment key={lang.name}>
                                        <Tooltip
                                            title={`${lang.name} (${getProcessStageLabel(lang.processStage)})`}
                                            arrow
                                        >
                                            <Chip
                                                label={getChipLabel(lang.name, lang.processStage)}
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent bubbling
                                                    e.preventDefault();
                                                    handleLanguageCountClick(e); // Call the same handler
                                                }}
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    backgroundColor: `${getChipColor(lang.processStage, theme.palette.mode)}20`,
                                                    color: getChipColor(lang.processStage, theme.palette.mode),
                                                    borderColor: getChipColor(lang.processStage, theme.palette.mode),
                                                    border: '1px solid',
                                                    fontWeight: 500,
                                                    borderRadius: '16px',
                                                    cursor: 'pointer', // Add pointer cursor
                                                }}
                                            />
                                        </Tooltip>
                                        <TaskChipIndicator contentId={file.data} language={lang.name} />
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        -
                                    </Typography>
                                )}
                                {filteredLanguages.length > 3 && (
                                    <Tooltip title={`${filteredLanguages.length - 3} more languages`}>
                                        <Chip
                                            label={`+${filteredLanguages.length - 3}`}
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                handleLanguageCountClick(e);
                                            }}
                                            sx={{
                                                fontSize: '0.7rem',
                                                backgroundColor: 'rgba(0,0,0,0.08)',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </>
                        )}
                        {type === FileTypes.AUDIO && <AudioFailedChip file={file} />}
                        {type === FileTypes.TEXT && file.translations && (
                            <>
                                {Object.keys(file.translations).length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {Object.entries(file.translations)
                                            .slice(0, 3)
                                            .map(([langName, langData]) => (
                                                <React.Fragment key={langName}>
                                                <Tooltip
                                                    title={`${langName} (${getProcessStageLabel(getEffectiveTextProcessStage(langData))})${
                                                        isSourceLanguage(langData) ? ' • Source Language' : ''
                                                    }`}
                                                    arrow
                                                >
                                                    <Chip
                                                        label={getTextChipLabel(langName, langData)}
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            handleLanguageCountClick(e);
                                                        }}
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            backgroundColor: `${getChipColor(getEffectiveTextProcessStage(langData), theme.palette.mode)}20`,
                                                            color: getTextChipColor(getEffectiveTextProcessStage(langData), theme.palette.mode),
                                                            borderColor: getTextChipColor(getEffectiveTextProcessStage(langData), theme.palette.mode),
                                                            border: '1px solid',
                                                            fontWeight: 500,
                                                            borderRadius: '16px',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </Tooltip>
                                                <TaskChipIndicator contentId={file.data} language={langName} />
                                                </React.Fragment>
                                            ))}
                                        {Object.keys(file.translations).length > 3 && (
                                            <Tooltip
                                                title={`${Object.keys(file.translations).length - 3} more languages`}
                                            >
                                                <Chip
                                                    label={`+${Object.keys(file.translations).length - 3}`}
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        handleLanguageCountClick(e);
                                                    }}
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                                        color: 'text.secondary',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        fontWeight: 500,
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        -
                                    </Typography>
                                )}
                            </>
                        )}
                        {type === FileTypes.BOOK && (
                            <>
                                {file.targetLanguageStatus && Object.keys(file.targetLanguageStatus).length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {Object.entries(file.targetLanguageStatus)
                                            .slice(0, 3)
                                            .map(([langName, langStatus]) => (
                                                <React.Fragment key={langName}>
                                                <Tooltip
                                                    title={`${langName} (${getBookLanguageStageLabel(langStatus?.processStage)})`}
                                                    arrow
                                                >
                                                    <Chip
                                                        label={getBookLanguageChipLabel(langName, langStatus?.processStage)}
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            handleLanguageCountClick(e);
                                                        }}
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            backgroundColor: `${getBookLanguageChipColor(langStatus?.processStage, theme.palette.mode)}20`,
                                                            color: getBookLanguageChipColor(langStatus?.processStage, theme.palette.mode),
                                                            borderColor: getBookLanguageChipColor(langStatus?.processStage, theme.palette.mode),
                                                            border: '1px solid',
                                                            fontWeight: 500,
                                                            borderRadius: '16px',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </Tooltip>
                                                <TaskChipIndicator contentId={file.data} language={langName} />
                                                </React.Fragment>
                                            ))}
                                        {Object.keys(file.targetLanguageStatus).length > 3 && (
                                            <Tooltip title={`${Object.keys(file.targetLanguageStatus).length - 3} more languages`}>
                                                <Chip
                                                    label={`+${Object.keys(file.targetLanguageStatus).length - 3}`}
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        handleLanguageCountClick(e);
                                                    }}
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                                        color: 'text.secondary',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        fontWeight: 500,
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                ) : (
                                    <Chip
                                        label={file.processStage || 'UPLOADED'}
                                        size="small"
                                        sx={{
                                            fontSize: '0.7rem',
                                            backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12),
                                            color: 'success.main',
                                            border: '1px solid',
                                            borderColor: (theme) => alpha(theme.palette.success.main, 0.35),
                                            fontWeight: 500,
                                            borderRadius: '16px',
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={6} sm={2} md={1}>
                    <Box className="file-folder-duration" sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimerIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {type === FileTypes.VIDEO ? formatDuration(file.duration) : '-'}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={6} sm={2} md={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} className="file-folder-updated-by">
                        <PersonIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {formatUserName(updatedBy)}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={6} sm={2} md={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} className="file-folder-updated-at">
                        <AccessTimeFilledIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {formatDate(file.updatedAt || file.updatedAt)}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={6} sm={2} md={1} className="list-file-menu">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                            size="small"
                            onClick={handleMenuClick}
                            data-testid={`file-menu-${file.videoName || file.name}`}
                            aria-label="more options"
                            className="file-folder-action-menu-button"
                            sx={{
                                opacity: { xs: 1, sm: 0.5 }, // More visible on mobile
                                visibility: 'visible',
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.08)',
                                },
                            }}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Grid>
            </Grid>

            <LanguagePopover
                open={languagePopover.open}
                anchorEl={languagePopover.anchorEl}
                onClose={handleLanguagePopoverClose}
                languages={
                    type === FileTypes.TEXT
                        ? file.translations || {}
                        : type === FileTypes.BOOK
                        ? file.targetLanguageStatus || {}
                        : filteredLanguages
                }
                videoLanguages={file.videoLanguages}
                isTextFile={type === FileTypes.TEXT}
                isBookFile={type === FileTypes.BOOK}
            />
        </Card>
    );
};

export default FileListItem;
